import { useEffect } from "react";

const CONFIRM_LABEL = /^(ok|yes|continue|confirm|start|save|create|apply|done)$/i;

function openModals() {
    return [...document.querySelectorAll(".MuiModal-root")].filter((el) => {
        if (el.classList.contains("MuiModal-hidden")) return false;
        if (el.getAttribute("aria-hidden") === "true") return false;
        const style = window.getComputedStyle(el);
        return style.display !== "none" && style.visibility !== "hidden";
    });
}

function isMenu(modal) {
    return Boolean(modal.querySelector("[role='listbox'], .MuiMenu-list, .MuiAutocomplete-listbox"));
}

function isTyping(target) {
    if (!(target instanceof HTMLElement)) return false;
    if (target.isContentEditable) return true;
    return target.tagName === "TEXTAREA";
}

function isControl(target) {
    if (!(target instanceof HTMLElement)) return false;
    return Boolean(target.closest("button, a, [role='button'], [role='menuitem'], [role='option']"));
}

function visibleButtons(modal) {
    return [...modal.querySelectorAll("button")].filter((button) => {
        if (button.disabled) return false;
        const style = window.getComputedStyle(button);
        return style.display !== "none" && style.visibility !== "hidden";
    });
}

function clickPrimary(modal) {
    const buttons = visibleButtons(modal);
    if (!buttons.length) return;
    const labeled = buttons.find((button) => CONFIRM_LABEL.test((button.textContent || "").trim()));
    const contained = [...buttons].reverse().find((button) => button.classList.contains("MuiButton-contained"));
    (labeled || contained || buttons[buttons.length - 1]).click();
}

function clickDismiss(modal) {
    const close = modal.querySelector("button[aria-label='Close'], button[aria-label='close']");
    if (close && !close.disabled) {
        close.click();
        return;
    }
    const cancel = visibleButtons(modal).find((button) => /^(cancel|no|close)$/i.test((button.textContent || "").trim()));
    if (cancel) {
        cancel.click();
        return;
    }
    const backdrop = modal.querySelector(".MuiBackdrop-root");
    if (backdrop) backdrop.click();
}

/**
 * Enter confirms the top popup (OK / Continue / Save). Escape closes it.
 * Menus and text areas keep their own keys.
 */
export function usePopupKeyboard() {
    useEffect(() => {
        const onKey = (event) => {
            if (event.key !== "Enter" && event.key !== "Escape") return;
            if (event.defaultPrevented || event.repeat) return;
            const modals = openModals();
            const top = modals[modals.length - 1];
            if (!top || isMenu(top)) return;
            if (event.key === "Enter") {
                if (isTyping(event.target) || isControl(event.target)) return;
                event.preventDefault();
                event.stopPropagation();
                clickPrimary(top);
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            clickDismiss(top);
        };
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, []);
}
